import { useEffect, useState, useCallback } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import "../../AppPage.css";
import { useNavigate, useParams } from "react-router-dom";
import { useGlimpsedFiles } from "../../../store/filteredFiles/hooks";
import { useMultiFileSelect } from "../../../store/multiFileSelect/hooks";
import { useAuth } from "../../../store/auth/hooks";
import { ContentTitleBar } from "../../../components/ContentTitleBar";
import SimpleAudioPlayer from "../../../components/editor/SimpleAudioPlayer";
import { ExpandMore } from "@mui/icons-material";
import { SERVER_URL } from "../../../store/config";
import GlimpsedFileAudioControl from "../../../components/glimpse/GlimpsedFileAudioControl";
import GlimpsedFileAudioTime from "../../../components/glimpse/GlimpsedFileAudioTime";
import { ClipTitleBarMenu } from "../../../components/titleBarMenu/ClipTitleBarMenu";
import {
  checkStatusWhitelist,
  getNavigationUrl,
} from "../../../store/navigationUtil";
import { canEditClip } from "../../../store/AclUtil";
import { loadClip, loadMultipleGuids } from "../utils/dataLoader";
import {
  GLIMPSE_COMPLETE,
  ASI_FILTER_COMPLETE,
  NLMS_FILTER_COMPLETE,
  STATUS_LABELS,
} from "../../../store/statuses";
import { HistoryDrawer } from "../../../components/HistoryDrawer";

const sortNLMSFiles = (a, b) => {
  const getPriority = (name) =>
    name.includes("noise_reduced")
      ? 1
      : name.includes("signal_enhanced")
        ? 2
        : 0;
  return getPriority(a.fileName) - getPriority(b.fileName);
};

const separateFileParts = (fileName) => {
  const match = fileName.match(/^GLIMPSED_(\d+)(?:_(\d+))?\.wav$/);

  if (!match) return { frameSize: 0, adaptRate: 0 };

  return {
    frameSize: Number(match[1]),
    adaptRate: match[2] ? Number(match[2]) : 0,
  };
};

const sortGlimpseFiles = (a, b) => {
  const aParts = separateFileParts(a.fileName);
  const bParts = separateFileParts(b.fileName);

  return (
    aParts.frameSize - bParts.frameSize || aParts.adaptRate - bParts.adaptRate
  );
};

export default function FilteredFilesPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    reset: filteredFilesReset,
    setActiveFilteredFiles,
    setPlayStateFilteredFiles,
    setClipTimeFilteredFiles,
  } = useGlimpsedFiles();
  const { selectFilteredFile } = useMultiFileSelect();
  const { user } = useAuth();
  const { caseId, clipId } = useParams();
  const [clip, setClip] = useState(null);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [selected, setSelected] = useState(false);

  const load = useCallback(async () => {
    console.log("Loading Glimpsed Files");
    await filteredFilesReset();
    const currClip = await loadClip(caseId, clipId);
    if (!currClip) {
      navigate(getNavigationUrl(caseId));
    }
    if (
      !checkStatusWhitelist(currClip, [
        GLIMPSE_COMPLETE,
        NLMS_FILTER_COMPLETE,
        ASI_FILTER_COMPLETE,
      ])
    ) {
      navigate(getNavigationUrl(caseId, currClip));
    }
    const guids = await loadMultipleGuids(caseId, clipId);
    console.log(guids);
    if (!guids) {
      // very weird issue here in the state of the file, maybe show an error page?
    }

    const STATUS_CONFIG = {
      [GLIMPSE_COMPLETE]: {
        filterFn: (g) =>
          g.fileName.includes("GLIMPSED") || g.fileName.includes("AUDIO_CH001"),
        sortFn: sortGlimpseFiles,
        mapFn: (g) => ({ ...g, editable: !g.fileName.includes("AUDIO") }),
        activeIndex: 1,
      },
      [ASI_FILTER_COMPLETE]: {
        filterFn: (g) => g.fileName.includes("AUDIO"),
        sortFn: null,
        mapFn: (g) => ({
          ...g,
          editable:
            g.fileName.includes("noise_reduced") ||
            g.fileName.includes("removed"),
        }),
        activeIndex: 1,
      },
      [NLMS_FILTER_COMPLETE]: {
        filterFn: (g) => g.fileName.includes("AUDIO_CH001"),
        sortFn: sortNLMSFiles,
        mapFn: (g) => ({
          ...g,
          editable:
            g.fileName.includes("noise_reduced") ||
            g.fileName.includes("signal_enhanced"),
        }),
        activeIndex: 1,
      },
    };

    const config = STATUS_CONFIG[currClip.status];

    if (config) {
      let visibleGuids = guids.filter(config.filterFn);
      if (config.sortFn) {
        visibleGuids = visibleGuids.sort(config.sortFn);
      }

      const toFilter = visibleGuids.map(config.mapFn);

      setClip(currClip);
      setFilteredFiles(toFilter);

      if (toFilter.length > 0) {
        const targetIdx = Math.min(config.activeIndex, toFilter.length - 1);
        setActiveFilteredFiles(toFilter[targetIdx]?.id);
      }
    } else {
      console.log(
        `Clip status ${currClip.status} does not have filtered files to show`,
      );
    }
  }, [caseId, clipId, navigate, filteredFilesReset, setActiveFilteredFiles]);

  useEffect(() => {
    load();
  }, [load]);

  const dataChange = () => {
    load();
  };

  const selectGlimpseFile = async (data) => {
    stopPlayback();
    setSelected(true);
    await selectFilteredFile(caseId, clipId, data);
    navigate(`/app/cases/${caseId}/clips/${clipId}`);
  };

  const getTrack = (data) => {
    return SERVER_URL + "/api/player/audio/" + data.guid;
  };

  const stopPlayback = () => setPlayStateFilteredFiles("STOP");

  const onJumpBack = (currentTime) =>
    setClipTimeFilteredFiles(currentTime, clip);

  const onJumpForward = (currentTime) =>
    setClipTimeFilteredFiles(currentTime, clip);

  return (
    clip && (
      <Stack component="main" height={1} overflow="hidden">
        {selected ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            flex={1}
            gap={2}
            my={4}
          >
            <CircularProgress />
            <Typography variant="caption">Loading...</Typography>
          </Stack>
        ) : (
          <Stack overflow="hidden" p={2}>
            <GlimpsedFileAudioTime />
            <Box
              pb="60px"
              pt={1}
              height="calc(100vh - 200px)"
              sx={{
                overflowY: "scroll",
              }}
            >
              {filteredFiles.map((data) => {
                const fileName = data?.fileName || "";
                const nameWithoutExt =
                  fileName.split("_").pop()?.split(".")[0] || "";

                const STATUS_MAP = {
                  removed: "removed",
                  noise_reduced: "noise reduced",
                  signal_enhanced: "signal enhanced",
                };

                let selectBtnText = nameWithoutExt;

                if (clip?.status === GLIMPSE_COMPLETE) {
                  selectBtnText = `Aperture Control ${nameWithoutExt}`;
                } else {
                  const matchedKey = Object.keys(STATUS_MAP).find((key) =>
                    fileName.includes(key),
                  );
                  if (matchedKey) selectBtnText = STATUS_MAP[matchedKey];
                }

                const SUFFIX_MAP = {
                  [ASI_FILTER_COMPLETE]: " (spectral)",
                  [NLMS_FILTER_COMPLETE]: " (temporal)",
                };

                if (SUFFIX_MAP[clip?.status]) {
                  selectBtnText += SUFFIX_MAP[clip?.status];
                }

                return (
                  <Accordion
                    key={data.id}
                    defaultExpanded
                    sx={{
                      backgroundColor: theme.palette.grey[100],
                      borderRadius: 1,
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="caption">{data.fileName}</Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      onClick={() => setActiveFilteredFiles(data.id)}
                    >
                      <SimpleAudioPlayer
                        key={data.id}
                        id={data.id}
                        barGap={0}
                        height={50}
                        waveColor="rgb(31, 78, 179)"
                        progressColor="rgb(31, 78, 179)"
                        cursorColor="rgb(255, 0, 0)"
                        url={getTrack(data)}
                        clip={clip}
                      />
                      {canEditClip(user, clip) && (
                        <Box
                          display="flex"
                          justifyContent="end"
                          alignItems="center"
                          mt={2}
                        >
                          {data.editable && (
                            <Button onClick={() => selectGlimpseFile(data)}>
                              Select {selectBtnText} Output
                            </Button>
                          )}
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </Stack>
        )}
        <Paper
          sx={{
            display: "flex",
            alignItems: "center",
            width: "calc(100% - 2rem)",
            position: "absolute",
            bottom: 80,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: "2",
            backgroundColor: theme.palette.common.white,
          }}
        >
          <GlimpsedFileAudioControl
            clipDuration={clip?.audioFiles[0]?.duration}
            onJumpBack={onJumpBack}
            onJumpForward={onJumpForward}
          />
        </Paper>
        <ContentTitleBar
          menu={
            <ClipTitleBarMenu
              color="light"
              user={user}
              clip={clip}
              readOnly={!canEditClip(user, clip)}
              onChange={dataChange}
              navigateOnDelete
            />
          }
          name={clip?.description}
          label={`${STATUS_LABELS[clip.status]} Audio Files`}
          onBack={() => navigate(`/app/cases/${caseId}/clips`)}
        />
        <Box
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.drawer,
          }}
        >
          <HistoryDrawer caseId={caseId} clipId={clipId} page="filteredFiles" />
        </Box>
      </Stack>
    )
  );
}
